import AddToBasketAPIAdaptor from 'adaptors/addToBasket/AddToBasketAPIAdaptor';
import LoggerFactory from 'utilities/loggerv2/loggerFactory';
import ErrorCodes from 'utilities/loggerv2/errorCodes';
import { FSPResponseError } from 'types/FSPTypes';
import { atgOrigin } from '../../utilities/util';
import { startSpan, endSpan, failSpan } from '../../utilities/loggerv2/performanceSpans';

const loggerATB = LoggerFactory.getLogger('Add to basket service');

function formatError(error: unknown, defaultCode = 'UNKNOWN_ERROR'): Error {
  // Convert to Error if not already
  const errorObj = error instanceof Error ? error : new Error(String(error || 'Unknown error'));

  // Ensure code property exists
  if (
    !Object.prototype.hasOwnProperty.call(errorObj, 'code') ||
    (errorObj as any).code === undefined ||
    (errorObj as any).code === null
  ) {
    (errorObj as any).code = defaultCode;
  }

  return errorObj;
}

const addItemToBasket = async (
  window: Window,
  csrfToken: string,
  sessionConfirmationNumber: string,
  optionNumber: string,
  personalisation?: string,
  warrantyOptionNumber?: string,
  selectedAddOnServices?: string[],
): Promise<string> => {
  const spanId = startSpan('addToBasket', {
    productSku: optionNumber,
    hasPersonalisation: !!personalisation,
    hasWarranty: !!warrantyOptionNumber,
    hasAddons: !!(selectedAddOnServices && selectedAddOnServices.length > 0),
  });

  try {
    const requestBody: {
      variantId: string;
      personalisation?: string;
      warrantyOptionNumber?: string;
      selectedAddOnServices?: string[];
    } = {
      variantId: optionNumber,
    };
    if (personalisation) {
      requestBody.personalisation = personalisation;
    }
    if (warrantyOptionNumber) {
      requestBody.warrantyOptionNumber = warrantyOptionNumber;
    }
    if (selectedAddOnServices) {
      requestBody.selectedAddOnServices = selectedAddOnServices;
    }

    const response = await fetch(
      `${atgOrigin(
        window,
      )}/rest/model/atg/commerce/order/purchase/CartModifierActor/addVariantToOrder?_dynSessConf=${sessionConfirmationNumber}`,
      {
        headers: {
          'Content-Type': 'application/json',
          'x-csrf-token': csrfToken,
        },
        method: 'POST',
        body: JSON.stringify(requestBody),
      },
    );

    const responseOk = response.ok !== undefined ? response.ok : true;

    const data = await response.json();

    // TODO: This is the first "skyscape" style error object we're receiving.
    // When we move off ATG, this file will need to be refactored to handle new error structures accordingly.
    const hasErrors = Array.isArray(data.errors);

    let fspErrorResponse: FSPResponseError | undefined;
    const fspErrorResponseCodes = ['fast_selling_item_sold_out', 'max_quantity_exceeded'];

    if (hasErrors) {
      fspErrorResponse = data.errors.find((e: FSPResponseError) => fspErrorResponseCodes.includes(e.key));
    }

    if (!responseOk && response.status === 400 && fspErrorResponse && fspErrorResponse.errors.length) {
      const message = fspErrorResponse.errors[0];
      const errorObj = new Error(message);
      const errorType =
        fspErrorResponse.key === 'fast_selling_item_sold_out' ? 'FSP_OUT_OF_STOCK' : 'FSP_MAX_QUANTITY_EXCEEDED';

      Object.defineProperty(errorObj, 'code', {
        value: errorType,
        writable: true,
        enumerable: true,
      });

      (await loggerATB).logWithCode(ErrorCodes.BASKET_SERVICE_ERROR, errorObj);

      failSpan(spanId, {
        errorType,
        status: response.status,
        statusText: response.statusText,
        url: `${atgOrigin(window)}/rest/model/atg/commerce/order/purchase/CartModifierActor/addVariantToOrder`,
      });

      return message;
    }

    // Check for HTTP error status codes first (like 404)
    if (!responseOk) {
      const errorMessage = `HTTP error ${response.status ? response.status : 'Unknown'}: ${response.statusText ? response.statusText : 'Unknown'}`;
      const errorObj = new Error(errorMessage);
      // Add a code property to the error object
      try {
        // Add code property with safety
        Object.defineProperty(errorObj, 'code', {
          value: response.status ? response.status.toString() : 'UNKNOWN_HTTP_ERROR',
          writable: true,
          enumerable: true,
        });

        // Log with safety checks
        const logger = await loggerATB;
        if (logger && typeof logger.logWithCode === 'function') {
          logger.logWithCode(
            ErrorCodes.BASKET_SERVICE_ERROR || {
              code: 'PRDFE-06001',
              message: 'HTTP error in basket service',
              level: 3,
              category: 'BASKET',
              description: 'An HTTP error occurred in the basket service',
            },
            errorObj,
          );
        }
      } catch (loggingError) {
        console.error('Failed to log HTTP error:', loggingError);
        console.error('Original error:', errorMessage);
      }

      failSpan(spanId, {
        errorType: ErrorCodes.BASKET_SERVICE_ERROR.code,
        status: response.status || 'Unknown',
        statusText: response.statusText || 'Unknown',
        url: `${atgOrigin(window)}/rest/model/atg/commerce/order/purchase/CartModifierActor/addVariantToOrder`,
      });
      // Return error message instead of throwing
      return 'There has been a technical issue. Please reload the page and try again or try again later.';
    }

    // Check for API errors in successful responses
    if (data.formError || (data.formExceptions && data.formExceptions.length > 0)) {
      const errorMessages = data.formExceptions
        ? data.formExceptions.map((ex: any) => ex.localizedMessage || 'Unknown error').join(', ')
        : 'Unknown API error';

      const errorCodes = data.formExceptions
        ? data.formExceptions.map((ex: any) => ex.errorCode || 'unknown')
        : ['unknown'];
      const errorObj = new Error(errorMessages);
      (errorObj as any).code = errorCodes[0] || 'UNKNOWN_API_ERROR';

      (await loggerATB).logWithCode(ErrorCodes.BASKET_API_ERROR, errorObj);

      failSpan(spanId, {
        errorType: 'API_ERROR',
        errorCodes,
        messages: errorMessages,
        formError: data.formError || false,
        concurrentUpdate: data.concurrentUpdate || false,
      });
      // Return error message instead of throwing
      return 'There has been a technical issue. Please reload the page and try again or try again later.';
    }
    endSpan(spanId, { success: true });

    return AddToBasketAPIAdaptor(data);
  } catch (error) {
    const errorObj = formatError(error, 'UNKNOWN_ERROR');

    (await loggerATB).logWithCode(ErrorCodes.BASKET_SERVICE_ERROR, errorObj);

    failSpan(spanId, {
      errorType: 'SERVICE_ERROR',
      message: errorObj.message || 'Unknown error',
      stack: errorObj.stack || '',
      errorCode: (errorObj as any).code || 'UNKNOWN_ERROR',
    });

    return 'There has been a technical issue. Please reload the page and try again or try again later.';
  }
};

export default addItemToBasket;
